import type { UseMutationResult } from '@tanstack/react-query';
import { ConfirmDialog } from '@/components/common/ConfirmDialog';
import { formatDate } from '@/lib/utils/date.utils';
import { getApiErrorMessage } from '@/lib/utils/error.utils';
import type { BillingChange } from '@/lib/types/billing';
import { BILLING_STRINGS } from '../constants/billing-strings';

const COPY = BILLING_STRINGS.AUTO_RENEW;

/**
 * The two directions, resolved once from `turningOn` rather than re-tested for
 * each prop (mnr-standards: no repeated ternary on one discriminant). Adding a
 * third state later means one more entry here, not three more ternaries.
 */
const MODES = {
  off: {
    title: COPY.OFF_TITLE,
    body: COPY.OFF_BODY,
    confirm: COPY.OFF_CONFIRM,
    dismiss: COPY.OFF_DISMISS,
    confirmVariant: 'navy',
  },
  on: {
    title: COPY.ON_TITLE,
    body: COPY.ON_BODY,
    confirm: COPY.ON_CONFIRM,
    dismiss: COPY.ON_DISMISS,
    confirmVariant: 'green',
  },
} as const;

export interface AutoRenewDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Which way the toggle is being moved. */
  turningOn: boolean;
  /** The end of the current period - the date either outcome takes effect. */
  periodEndsAt: string | null;
  mutation: UseMutationResult<BillingChange, unknown, boolean>;
}

/**
 * Confirms an auto-renewal change (FR-41, MYN-113.3).
 *
 * Confirmed rather than applied straight from the toggle because turning it off
 * is the quiet half of cancelling: nothing visibly changes today, and the
 * consequence arrives a month later when access stops. The date is named for
 * exactly that reason.
 */
export function AutoRenewDialog({
  open,
  onOpenChange,
  turningOn,
  periodEndsAt,
  mutation,
}: AutoRenewDialogProps) {
  const mode = MODES[turningOn ? 'on' : 'off'];
  const date = periodEndsAt ? formatDate(periodEndsAt) : null;

  return (
    <ConfirmDialog
      open={open}
      onOpenChange={onOpenChange}
      title={mode.title}
      description={date ? mode.body.replace('{date}', date) : undefined}
      confirmLabel={mode.confirm}
      cancelLabel={mode.dismiss}
      confirmVariant={mode.confirmVariant}
      layout="stacked"
      align="center"
      isConfirming={mutation.isPending}
      onConfirm={() => mutation.mutate(turningOn, { onSuccess: () => onOpenChange(false) })}
    >
      {mutation.isError && (
        <p role="alert" className="text-danger text-sm">
          {getApiErrorMessage(mutation.error, mode.confirm)}
        </p>
      )}
    </ConfirmDialog>
  );
}
